import { useRef, useState } from "react";
import { Image as ImageIcon, Loader2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function ImageUploadField({ value, onChange, label = "Image", helper, testid = "image-upload" }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState("");

  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  return (
    <div>
      <label className="mb-2 block font-body text-[10px] tracking-[0.3em] uppercase text-white/45">
        {label}
      </label>
      {value ? (
        <div className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02]">
          <img src={value} alt="" className="h-56 w-full object-cover" />
          <button
            type="button"
            onClick={() => onChange("")}
            data-testid={`${testid}-remove`}
            className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full border border-white/20 bg-black/60 text-white/85 backdrop-blur transition hover:bg-black/80"
            aria-label="Remove image"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="absolute inset-x-0 bottom-0 truncate bg-gradient-to-t from-black/80 to-transparent px-3 py-2 font-mono text-[10px] text-white/60">
            {value}
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          data-testid={`${testid}-trigger`}
          className="flex h-56 w-full flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-white/15 bg-white/[0.02] text-white/55 transition hover:border-[#0055ff]/40 hover:bg-white/[0.04] hover:text-white"
        >
          {uploading ? (
            <Loader2 className="h-6 w-6 animate-spin" />
          ) : (
            <>
              <ImageIcon className="h-6 w-6" />
              <span className="font-body text-sm">Click to upload</span>
              <span className="font-body text-[11px] text-white/40">PNG, JPG, WEBP · max 10 MB</span>
            </>
          )}
        </button>
      )}
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml,image/avif"
        onChange={handleFile}
        className="hidden"
        data-testid={`${testid}-input`}
      />
      <div className="mt-2 flex gap-2">
        <input
          type="text"
          value={urlInput}
          onChange={(e) => setUrlInput(e.target.value)}
          placeholder="or paste an image URL…"
          className="flex-1 rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2 font-body text-xs text-white placeholder-white/30 focus:border-[#0055ff]/50 focus:outline-none"
        />
        <button
          type="button"
          onClick={() => {
            if (urlInput.trim()) {
              onChange(urlInput.trim());
              setUrlInput("");
            }
          }}
          className="rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2 font-body text-xs text-white/75 hover:border-white/25 hover:text-white"
        >
          <Upload className="h-3.5 w-3.5" />
        </button>
      </div>
      {helper && <p className="mt-1.5 font-body text-[11px] text-white/40">{helper}</p>}
    </div>
  );
}
